import { Download, FileCheck, Sparkles, Clock, Layers, Users, ArrowRight } from "lucide-react";

const BADGES = [
  "K-12 Ready",
  "Workforce Development",
  "Career Readiness",
  "Leadership Development",
];

const HIGHLIGHTS = [
  { icon: FileCheck, t: "Turnkey curriculum", d: "21 sessions, ready to deliver — no lesson-plan rebuilds." },
  { icon: Clock,     t: "Low-prep implementation", d: "Most teachers run it in 20–45 min blocks." },
  { icon: Layers,    t: "Standards alignment", d: "Designed to support Nevada CTE / Perkins V + CCTC." },
  { icon: Users,     t: "Classroom flexibility", d: "CTE elective, after-school club, or homeschool." },
  { icon: Sparkles,  t: "AI literacy by example", d: "Students learn responsible AI use through doing." },
  { icon: FileCheck, t: "District-ready reports", d: "CSV exports, share-link snapshots, and a public PDF." },
];

/**
 * EducatorsSection — clean, authoritative section for admins/teachers/
 * districts/nonprofits. Anchors the trust posture and includes the
 * one-page District PDF download.
 */
export default function EducatorsSection({ onBookDemo }) {
  return (
    <section
      id="educators"
      className="relative bg-[#FDFBF7] py-20 sm:py-28"
      data-testid="educators-section"
    >
      <div className="max-w-7xl mx-auto px-4 lg:px-6">
        {/* Hero panel — full-width educator illustration */}
        <div className="mb-12 rounded-3xl overflow-hidden ring-1 ring-black/10 shadow-[0_20px_50px_rgba(0,0,0,0.10)]">
          <img
            src="/assets/landing/educators-panel.png"
            alt="Teacher facilitating an entrepreneurship classroom workshop with students collaborating on business ideas with LemonBot."
            className="w-full h-auto block"
            loading="lazy"
            data-testid="educators-feature-image"
          />
        </div>

        <div className="grid lg:grid-cols-12 gap-10 items-start">
        {/* Left — copy */}
        <div className="lg:col-span-5">
          <p className="text-xs font-black tracking-[0.22em] uppercase text-gray-500 mb-3">
            For Educators &amp; Districts
          </p>
          <h2 className="text-[32px] md:text-5xl font-black tracking-tighter leading-[1.05] text-black">
            Built For{" "}
            <span className="lsbp-yellow-chip">Schools</span>
            <br /> And Youth Programs.
          </h2>
          <p className="mt-5 text-base md:text-xl text-gray-700 leading-relaxed">
            Administrators, teachers, districts, and nonprofits — start a
            pilot in days, not quarters. Designed with educator workflow at
            the core.
          </p>

          {/* Badges */}
          <ul className="mt-7 flex flex-wrap gap-2" data-testid="educators-badges">
            {BADGES.map((b) => (
              <li
                key={b}
                className="inline-flex items-center gap-1.5 bg-white ring-1 ring-black/10 text-black px-3 py-1.5 rounded-full text-xs font-extrabold"
              >
                <span className="w-1.5 h-1.5 rounded-full bg-[#00E65C]" aria-hidden="true" />
                {b}
              </li>
            ))}
          </ul>

          {/* CTAs */}
          <div className="mt-8 flex flex-col sm:flex-row gap-3">
            <a
              href="/api/public/district-overview.pdf"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center gap-2 bg-black text-white px-5 py-3 rounded-full text-lg font-bold hover:bg-[#FFD500] hover:text-black transition-colors"
              data-testid="educators-pdf-cta"
            >
              <Download className="w-4 h-4" /> Download District One-Pager
            </a>
            <button
              onClick={onBookDemo}
              className="inline-flex items-center justify-center gap-2 bg-white ring-2 ring-black text-black px-5 py-3 rounded-full text-lg font-bold hover:bg-[#FFD500] transition-colors"
              data-testid="educators-book-demo"
            >
              Book A Demo <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Right — highlight grid */}
        <ul
          className="lg:col-span-7 grid sm:grid-cols-2 gap-3 sm:gap-4"
          data-testid="educators-highlights"
        >
          {HIGHLIGHTS.map((h) => (
            <li
              key={h.t}
              className="bg-white ring-1 ring-black/10 rounded-2xl p-4 flex items-start gap-3 transition-all hover:-translate-y-1 hover:shadow-[0_12px_30px_rgba(0,0,0,0.06)]"
            >
              <span className="bg-[#FFD500] text-black w-10 h-10 rounded-xl flex items-center justify-center shrink-0">
                <h.icon className="w-4 h-4" />
              </span>
              <div className="pt-0.5">
                <p className="font-extrabold text-black text-sm">{h.t}</p>
                <p className="text-xs text-gray-600 leading-snug mt-0.5">{h.d}</p>
              </div>
            </li>
          ))}
        </ul>
      </div>
      </div>
    </section>
  );
}
